package io.github.erkko68.filament.compose.scene

import androidx.compose.runtime.Immutable
import androidx.compose.ui.graphics.colorspace.ColorSpaces
import io.github.erkko68.filament.utils.Float3
import io.github.erkko68.filament.utils.Quaternion
import io.github.erkko68.filament.utils.RotationsOrder
import io.github.erkko68.filament.utils.degrees
import io.github.erkko68.filament.utils.eulerAngles
import io.github.erkko68.filament.utils.quaternion
import io.github.erkko68.filament.utils.radians
import kotlin.math.abs
import kotlin.math.cos
import kotlin.math.pow
import kotlin.math.sin
// Aliased where the name would collide with the Rotation member of the same name.
import io.github.erkko68.filament.utils.angle as quatAngle
import io.github.erkko68.filament.utils.lookTowards as lookTowardsMatrix
import io.github.erkko68.filament.utils.slerp as quatSlerp

/**
 * A point in 3-D space.
 *
 * Distinct from [Direction], [Scale], and [LinearColor] so the compiler stops you passing one where
 * another is expected (they are all float-triples and trivially confused). All four are
 * **immutable**, which also makes them stable Compose inputs — passing them to scene composables
 * doesn't force needless recompositions the way the mutable [Float3] did.
 *
 * Interop with filament-utils vector math is a hop away: construct from a [Float3]
 * (`Position(myFloat3)`), or drop out with [toFloat3]/[Float3.toPosition]. Common in-domain math
 * (translate, scale) is available as operators so you rarely need to.
 */
@Immutable
data class Position(val x: Float, val y: Float, val z: Float) {
    /** Uniform value on all axes. */
    constructor(v: Float) : this(v, v, v)
    /** From a filament-utils [Float3]. */
    constructor(v: Float3) : this(v.x, v.y, v.z)

    operator fun plus(d: Direction) = Position(x + d.x, y + d.y, z + d.z)
    operator fun minus(d: Direction) = Position(x - d.x, y - d.y, z - d.z)
    /** Displacement from [other] to this point. */
    operator fun minus(other: Position) = Direction(x - other.x, y - other.y, z - other.z)

    fun toFloat3() = Float3(x, y, z)
}

/**
 * A direction / displacement vector in 3-D space. See [Position] for the rationale behind the
 * distinct type and the [Float3] interop story.
 */
@Immutable
data class Direction(val x: Float, val y: Float, val z: Float) {
    constructor(v: Float) : this(v, v, v)
    constructor(v: Float3) : this(v.x, v.y, v.z)

    operator fun plus(o: Direction) = Direction(x + o.x, y + o.y, z + o.z)
    operator fun minus(o: Direction) = Direction(x - o.x, y - o.y, z - o.z)
    operator fun times(s: Float) = Direction(x * s, y * s, z * s)
    operator fun div(s: Float) = Direction(x / s, y / s, z / s)
    operator fun unaryMinus() = Direction(-x, -y, -z)

    /** Euclidean length. */
    val length: Float get() = kotlin.math.sqrt(x * x + y * y + z * z)
    /** Unit-length copy, or this vector unchanged if it is zero-length. */
    fun normalized(): Direction = length.let { if (it > 0f) this / it else this }

    fun toFloat3() = Float3(x, y, z)

    /** The axis directions, in Filament's right-handed Y-up convention. */
    companion object {
        val Zero = Direction(0f, 0f, 0f)
        val Up = Direction(0f, 1f, 0f)
        val Down = Direction(0f, -1f, 0f)
        val Right = Direction(1f, 0f, 0f)
        val Left = Direction(-1f, 0f, 0f)
        /** Local −Z — the axis glTF and Filament treat as "forward". See [Rotation.lookTowards]. */
        val Forward = Direction(0f, 0f, -1f)
        val Back = Direction(0f, 0f, 1f)
    }
}

/**
 * A per-axis scale factor. See [Position] for the rationale behind the distinct type.
 */
@Immutable
data class Scale(val x: Float, val y: Float, val z: Float) {
    constructor(v: Float) : this(v, v, v)
    constructor(v: Float3) : this(v.x, v.y, v.z)

    operator fun times(s: Float) = Scale(x * s, y * s, z * s)
    operator fun times(o: Scale) = Scale(x * o.x, y * o.y, z * o.z)

    fun toFloat3() = Float3(x, y, z)
}

/**
 * A 3-D rotation, stored as a unit quaternion. See [Position] for the rationale behind the
 * distinct type — the same one applies doubly here, since filament-utils' [Quaternion] has
 * mutable components and so is an unstable Compose input.
 *
 * Build one with [axisAngle] or [euler] rather than the raw component constructor, and combine
 * with `*` (right operand applied first):
 *
 * ```kotlin
 * Cube(rotation = Rotation.axisAngle(Direction.Up, degrees = spin))
 * Cube(rotation = Rotation.euler(yaw = 45f) * Rotation.euler(pitch = 30f))
 *
 * // Aim an entity at a target, and blend towards a new pose over time
 * Cube(rotation = Rotation.lookTowards(target - turret))
 * Cube(rotation = Rotation.slerp(from, to, t))
 * ```
 *
 * [fromTo], [lookTowards], [slerp], [nlerp], [toEuler], [angleTo] and [normalized] cover the
 * usual scene work. **For anything beyond them** — matrix interop, swizzles, custom
 * interpolation — hop to filament-utils' [Quaternion] and back; the two are the same four
 * floats and the conversion is lossless:
 *
 * ```kotlin
 * val blended = Rotation(myOwnInterpolation(a.toQuaternion(), b.toQuaternion()))
 * val asRotation = someQuaternion.toRotation()   // or Rotation(someQuaternion)
 * ```
 *
 * Equality is component-wise, as for any data class — which is *not* the same as "same
 * orientation": `r` and its all-components-negated twin turn an entity to the identical pose but
 * compare unequal, and accumulated products drift. That is what you want for Compose (equal
 * values let a composable skip), but to ask whether two orientations agree use
 * [angleTo] against a tolerance rather than `==`.
 */
@Immutable
data class Rotation(val x: Float, val y: Float, val z: Float, val w: Float) {
    /** From a filament-utils [Quaternion]. */
    constructor(q: Quaternion) : this(q.x, q.y, q.z, q.w)

    /** Applies [other] and then this rotation (quaternion product, not commutative). */
    operator fun times(other: Rotation) = Rotation(
        w * other.x + x * other.w + y * other.z - z * other.y,
        w * other.y - x * other.z + y * other.w + z * other.x,
        w * other.z + x * other.y - y * other.x + z * other.w,
        w * other.w - x * other.x - y * other.y - z * other.z,
    )

    /** This rotation applied to a direction vector. */
    operator fun times(d: Direction): Direction {
        // v + s(w(u×v) + u×(u×v)) with u = (x,y,z). s = 2/‖q‖² rather than a bare 2, so a
        // rotation that has drifted off unit length still rotates instead of also scaling.
        val s = (x * x + y * y + z * z + w * w).let { if (it > 0f) 2f / it else 0f }
        val cx = y * d.z - z * d.y
        val cy = z * d.x - x * d.z
        val cz = x * d.y - y * d.x
        return Direction(
            d.x + s * (w * cx + y * cz - z * cy),
            d.y + s * (w * cy + z * cx - x * cz),
            d.z + s * (w * cz + x * cy - y * cx),
        )
    }

    /**
     * The inverse rotation — undoes this one. Exact for a drifted rotation too, where the bare
     * conjugate would leave the length error behind.
     */
    fun inverse(): Rotation {
        val n = x * x + y * y + z * z + w * w
        return if (n == 1f || n == 0f) Rotation(-x, -y, -z, w)
        else Rotation(-x / n, -y / n, -z / n, w / n)
    }

    /**
     * Euler angles in degrees, as `Direction(pitch, yaw, roll)` about X/Y/Z. The inverse of
     * [euler] — useful for debug readouts and for clamping an axis (e.g. pitch to ±89°).
     */
    fun toEuler(): Direction = eulerAngles(toQuaternion()).toDirection()

    /** The smallest angle in degrees between this orientation and [other]. */
    fun angleTo(other: Rotation): Float = degrees(quatAngle(toQuaternion(), other.toQuaternion()))

    /** Unit-length copy. Use it to shed drift after accumulating many products. */
    fun normalized(): Rotation {
        val n = kotlin.math.sqrt(x * x + y * y + z * z + w * w)
        return if (n > 0f) Rotation(x / n, y / n, z / n, w / n) else Identity
    }

    fun toQuaternion() = Quaternion(x, y, z, w)

    /**
     * As a **column-major 3×3** rotation matrix (9 floats) — the shape Filament's builders take,
     * e.g. `IndirectLight.Builder.rotation`.
     */
    fun toRotationMatrix() = floatArrayOf(
        1f - 2f * (y * y + z * z), 2f * (x * y + w * z), 2f * (x * z - w * y),
        2f * (x * y - w * z), 1f - 2f * (x * x + z * z), 2f * (y * z + w * x),
        2f * (x * z + w * y), 2f * (y * z - w * x), 1f - 2f * (x * x + y * y),
    )

    companion object {
        /** No rotation. The default for every scene composable's `rotation` argument. */
        val Identity = Rotation(0f, 0f, 0f, 1f)

        /**
         * [degrees] of rotation about [axis] (normalized for you), right-hand rule. A zero-length
         * [axis] names no rotation, so [Identity] comes back rather than a NaN that would
         * silently hide the entity.
         */
        fun axisAngle(axis: Direction, degrees: Float): Rotation {
            val a = axis.normalized()
            if (a == Direction.Zero) return Identity
            val h = radians(degrees) * 0.5f
            val s = sin(h)
            return Rotation(a.x * s, a.y * s, a.z * s, cos(h))
        }

        /**
         * From intrinsic Tait-Bryan angles in degrees, applied Z (roll) then Y (yaw) then
         * X (pitch) — filament-utils' default [RotationsOrder.ZYX].
         */
        fun euler(pitch: Float = 0f, yaw: Float = 0f, roll: Float = 0f) =
            Rotation(Quaternion.fromEuler(Float3(pitch, yaw, roll)))

        /**
         * The shortest-arc rotation taking [from] onto [to]. Length is irrelevant — only the
         * two directions are — so displacements like `target - muzzle` can be passed straight in.
         */
        fun fromTo(from: Direction, to: Direction) = Rotation(
            // Normalized here because fromRotation's shortest-arc formula assumes unit inputs:
            // feeding it raw displacements yields a plausible-looking but wrong angle.
            Quaternion.fromRotation(from.normalized().toFloat3(), to.normalized().toFloat3()),
        )

        /**
         * Aims an entity along [forward]: the rotation that takes local **−Z** (the glTF/Filament
         * "forward" axis) onto [forward], keeping local +Y as close to [up] as it can. This is the
         * turret / billboard / chase-camera building block.
         *
         * A [forward] parallel to [up] has no unique answer; rather than emit a NaN transform that
         * would silently hide the entity, another up axis is substituted.
         */
        fun lookTowards(forward: Direction, up: Direction = Direction.Up): Rotation {
            val f = forward.normalized()
            // Both normalized before the dot product — against a longer `up` it would exceed the
            // threshold for perfectly good non-parallel pairs and swap in the fallback axis.
            val n = up.normalized()
            val parallel = abs(f.x * n.x + f.y * n.y + f.z * n.z) > 0.9999f
            val u = if (!parallel) n
                else if (abs(f.y) > 0.9999f) Direction.Back else Direction.Up
            return Rotation(quaternion(lookTowardsMatrix(Float3(), f.toFloat3(), u.toFloat3())))
        }

        /**
         * Shortest-path spherical interpolation: `t = 0` gives [a], `t = 1` gives [b], and the
         * path between them turns at a constant rate. The one to reach for when blending poses.
         */
        fun slerp(a: Rotation, b: Rotation, t: Float) =
            Rotation(quatSlerp(a.toQuaternion(), b.toQuaternion(), t))

        /**
         * Normalized linear interpolation — cheaper than [slerp] but not constant-rate. Fine for
         * per-frame blending between nearby orientations. Takes the short way round, as [slerp]
         * does.
         */
        fun nlerp(a: Rotation, b: Rotation, t: Float): Rotation {
            // Negating b when the two face opposite hemispheres picks the shorter of the two
            // equivalent paths; without it the blend can spin most of the way around.
            val s = if (a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w < 0f) -t else t
            val k = 1f - t
            return Rotation(
                a.x * k + b.x * s, a.y * k + b.y * s, a.z * k + b.z * s, a.w * k + b.w * s,
            ).normalized()
        }
    }
}

/**
 * An RGB color (linear or sRGB depending on the consuming API). Distinct from the spatial
 * vectors so a [LinearColor] can't be passed as a [Position]. Components are [r]/[g]/[b].
 *
 * Deliberately **not** called `Color`: `androidx.compose.ui.graphics.Color` is in scope in
 * practically every file that uses this library, and two same-named colour types with different
 * transfer functions is a trap.
 *
 * Interop with Compose UI colours goes through [fromComposeColor] / [toComposeColor], which apply
 * the sRGB transfer function in each direction — Compose colours are gamma-encoded, so copying
 * their components across raw would wash the result out. Alpha is dropped; scene colours are RGB.
 *
 * ```kotlin
 * val tint = LinearColor.fromComposeColor(MaterialTheme.colorScheme.primary)
 * ```
 */
@Immutable
data class LinearColor(val r: Float, val g: Float, val b: Float) {
    /** Uniform value on all channels (grey). */
    constructor(v: Float) : this(v, v, v)
    /** From a filament-utils [Float3] (x→r, y→g, z→b), assumed already linear. */
    constructor(v: Float3) : this(v.x, v.y, v.z)

    operator fun times(s: Float) = LinearColor(r * s, g * s, b * s)
    operator fun plus(o: LinearColor) = LinearColor(r + o.r, g + o.g, b + o.b)

    fun toFloat3() = Float3(r, g, b)

    /**
     * As a gamma-encoded Compose UI colour (alpha 1), for feeding a scene colour back into
     * Compose UI. Channels are clamped to 0..1 — over-bright values lose their headroom.
     */
    fun toComposeColor(): androidx.compose.ui.graphics.Color =
        androidx.compose.ui.graphics.Color(
            linearToSrgb(r), linearToSrgb(g), linearToSrgb(b),
        )

    companion object {
        /**
         * Converts a Compose UI colour to linear space, dropping alpha. Handles any source colour
         * space (sRGB, Display P3, …) by converting through Compose's own colour management.
         */
        fun fromComposeColor(v: androidx.compose.ui.graphics.Color): LinearColor {
            val linear = v.convert(ColorSpaces.LinearSrgb)
            return LinearColor(linear.red, linear.green, linear.blue)
        }
    }
}

/** sRGB OETF — linear component to gamma-encoded. */
private fun linearToSrgb(c: Float): Float {
    val v = c.coerceIn(0f, 1f)
    return if (v <= 0.0031308f) v * 12.92f else 1.055f * v.pow(1f / 2.4f) - 0.055f
}

/**
 * Convert a Compose UI [androidx.compose.ui.graphics.Color] to a scene-ready [LinearColor],
 * applying the sRGB→linear transfer. Alpha is dropped. Covers ARGB ints too, via Compose's own
 * `Color(0xFF2196F3)` constructor.
 */
fun androidx.compose.ui.graphics.Color.toLinearColor(): LinearColor =
    LinearColor.fromComposeColor(this)

/** Reinterpret a [Float3] as a [Position]. */
fun Float3.toPosition() = Position(x, y, z)
/** Reinterpret a [Float3] as a [Direction]. */
fun Float3.toDirection() = Direction(x, y, z)
/** Reinterpret a [Float3] as a [Scale]. */
fun Float3.toScale() = Scale(x, y, z)
/** Reinterpret a [Float3] as a [LinearColor] (x→r, y→g, z→b). */
fun Float3.toLinearColor() = LinearColor(x, y, z)
/** Reinterpret a [Quaternion] as a [Rotation]. */
fun Quaternion.toRotation() = Rotation(x, y, z, w)
